import { EVENTS } from 'util.events'
import { executeJSmodules } from 'util.misc'

const pendingFocus = new Map()
const focusLifetime = 15000

class BPCollectionMobileDrawer extends HTMLElement {
  connectedCallback() {
    this.abortController?.abort()
    this.abortController = new AbortController()
    const { signal } = this.abortController
    this.main = this.closest('.bp-collection-main')
    this.trigger = this.querySelector('[data-bp-drawer-open]')
    this.drawer = this.querySelector('overlay-drawer')
    this.dialog = this.drawer?.querySelector('dialog')
    this.filters = this.querySelector('[data-bp-drawer-filters]')
    this.status = this.querySelector('[data-bp-drawer-status]')
    this.retry = this.querySelector('[data-bp-drawer-retry]')
    if (!this.main || !this.trigger || !this.dialog || !this.filters) return

    this.dialog.id = `${this.drawer.id}-dialog`
    this.trigger.setAttribute('aria-controls', this.dialog.id)
    this.media = window.matchMedia('(max-width: 768px)')
    this.focusKey = `bp-collection-drawer-focus:${window.location.pathname}:${this.dataset.sectionId}`
    this.setExpanded(false)

    this.trigger.addEventListener('click', () => {
      if (this.dialog.open) this.close()
      else this.open()
    }, { signal })
    this.retry.addEventListener('click', () => this.loadFilters(true), { signal })
    this.drawer.addEventListener('drawer:show', () => this.setExpanded(true), { signal })
    this.drawer.addEventListener('drawer:hide', () => this.setExpanded(false), { signal })
    this.drawer.addEventListener('drawer:after-hide', () => this.focusTrigger(), { signal })
    this.dialog.addEventListener('close', () => this.setExpanded(false), { signal })
    this.addEventListener('filter:selected', () => this.close(true), { capture: true, signal })
    this.addEventListener(EVENTS.sortSelected, () => {
      pendingFocus.delete(this.focusKey)
      try { sessionStorage.setItem(this.focusKey, String(Date.now())) } catch {}
      this.close(true)
    }, { capture: true, signal })

    const beforeUpdate = (event) => {
      if (!this.media.matches || !this.main.contains(event.target)) return
      if (!this.contains(event.target) && !this.dialog.open) return
      pendingFocus.set(this.focusKey, Date.now())
      this.close(true)
    }
    document.addEventListener(EVENTS.ajaxCollectionRender, beforeUpdate, { capture: true, signal })
    document.addEventListener('price-range:change', beforeUpdate, { capture: true, signal })
    document.addEventListener('collection:reloaded', (event) => {
      if (this.main.contains(event.target)) this.restorePendingFocus()
    }, { signal })
    this.media.addEventListener('change', () => {
      if (!this.media.matches) this.close(true, false)
    }, { signal })
    window.addEventListener('pagehide', () => this.close(true, false), { signal })
    queueMicrotask(() => this.restorePendingFocus())
  }

  disconnectedCallback() {
    this.abortController?.abort()
    if (this.dialog?.open) this.dialog.close()
    this.setExpanded(false)
  }

  setExpanded(isOpen) {
    this.trigger?.setAttribute('aria-expanded', String(isOpen))
    this.trigger?.classList.toggle('is-active', isOpen)
  }

  async open() {
    if (!this.media.matches || this.opening) return
    this.opening = true
    try {
      await customElements.whenDefined('overlay-drawer')
      if (!this.isConnected || !this.media.matches) return
      this.drawer.show()
      this.loadFilters()
    } finally {
      this.opening = false
    }
  }

  close(immediate = false, restoreFocus = true) {
    this.setExpanded(false)
    if (!this.dialog?.open) return
    if (immediate) {
      this.dialog.close()
      if (restoreFocus) this.focusTrigger()
    } else {
      this.drawer.hide()
    }
  }

  focusTrigger() {
    if (!this.isConnected || !this.media.matches || this.dialog.open) return
    this.trigger.focus({ preventScroll: true })
  }

  restorePendingFocus() {
    let timestamp = pendingFocus.get(this.focusKey)
    try {
      timestamp ||= Number(sessionStorage.getItem(this.focusKey))
      sessionStorage.removeItem(this.focusKey)
    } catch {}
    pendingFocus.delete(this.focusKey)
    if (timestamp && Date.now() - timestamp < focusLifetime) this.focusTrigger()
  }

  async loadFilters(force = false) {
    const query = window.location.search
    if (!force && this.loadedQuery === query) return
    if (this.loading) return this.loading
    this.status.hidden = false
    this.status.textContent = this.dataset.loadingLabel
    this.retry.hidden = true
    this.filters.setAttribute('aria-busy', 'true')
    this.loading = (async () => {
      try {
        const url = new URL(window.location.href)
        url.searchParams.set('section_id', 'item-grid-filters')
        const response = await fetch(url, { signal: this.abortController.signal })
        if (!response.ok) throw new Error('Filters unavailable')
        const html = new DOMParser().parseFromString(await response.text(), 'text/html')
        const nativeFilters = html.querySelector('.filter-wrapper')
        if (!nativeFilters || !this.isConnected) throw new Error('Filters unavailable')
        this.prepareFilters(nativeFilters)
        this.filters.replaceChildren(nativeFilters)
        executeJSmodules(this.filters.querySelectorAll('script[type="module"]'))
        this.loadedQuery = query
        this.status.hidden = true
      } catch (error) {
        if (error.name !== 'AbortError' && this.isConnected) {
          this.status.textContent = this.dataset.errorLabel
          this.retry.hidden = false
        }
      } finally {
        this.filters.setAttribute('aria-busy', 'false')
        this.loading = null
      }
    })()
    return this.loading
  }

  prepareFilters(nativeFilters) {
    if (this.dataset.enableSort === 'false') nativeFilters.querySelector('sort-by')?.remove()
    const ids = new Map()
    nativeFilters.querySelectorAll('[id]').forEach((element) => {
      const oldId = element.id
      const newId = `${oldId}-${this.dataset.sectionId}-bp-mobile`
      ids.set(oldId, newId)
      element.id = newId
    })
    nativeFilters.querySelectorAll('*').forEach((element) => {
      for (const attribute of ['for', 'form', 'list', 'aria-controls', 'aria-labelledby', 'aria-describedby']) {
        const value = element.getAttribute(attribute)
        if (value) element.setAttribute(attribute, value.split(/\s+/).map((id) => ids.get(id) || id).join(' '))
      }
      const href = element.getAttribute('href')
      if (href?.startsWith('#') && ids.has(href.slice(1))) element.setAttribute('href', `#${ids.get(href.slice(1))}`)
    })
    nativeFilters.querySelectorAll('a.filter-sort').forEach((link) => {
      const button = document.createElement('button')
      for (const attribute of link.attributes) {
        if (attribute.name !== 'href') button.setAttribute(attribute.name, attribute.value)
      }
      button.type = 'button'
      button.setAttribute('aria-pressed', String(button.classList.contains('element-chip--active')))
      button.append(...link.childNodes)
      link.replaceWith(button)
    })
  }
}

if (!customElements.get('bp-collection-mobile-drawer')) {
  customElements.define('bp-collection-mobile-drawer', BPCollectionMobileDrawer)
}
